import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/renew-santulan-logo.png";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    } else {
      // Already on home page, just scroll
      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/50 backdrop-blur-md border-b border-border shadow-sm">
      <div className="container-custom py-3 px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <a 
            href="/#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('hero');
            }}
            className="cursor-pointer"
            aria-label="Go to home"
          >
            <img 
              src={logo} 
              alt="Renew Santulan Logo" 
              className="h-16 sm:h-24 md:h-20 w-auto transition-transform hover:scale-105 -my-2"
            />
          </a>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex gap-6 xl:gap-8 items-center ml-auto mr-4">
            <a 
              href="/#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('hero');
              }}
              className="text-base lg:text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Home
            </a>
            <Link 
              to="/about"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-base lg:text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              About Me
            </Link>
            <a 
              href="/#programs"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('programs');
              }}
              className="text-base lg:text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Programs
            </a>
            <a 
              href="/#testimonials"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('testimonials');
              }}
              className="text-base lg:text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Testimonials
            </a>
            <a 
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
              }}
              className="text-base lg:text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </a>
            <a
              href="/#book-session"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('book-session');
              }}
            >
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Book Now
              </Button>
            </a>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-foreground hover:bg-muted rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile/Tablet Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full bg-background backdrop-blur-lg border-b border-border shadow-lg animate-in slide-in-from-top-2">
            <div className="flex flex-col p-4 space-y-3">
              <a 
                href="/#hero"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('hero');
                }}
                className="text-base text-muted-foreground hover:text-foreground transition-colors py-3 px-4 hover:bg-muted rounded-lg text-left"
              >
                Home
              </a>
              <Link 
                to="/about"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-base text-muted-foreground hover:text-foreground transition-colors py-3 px-4 hover:bg-muted rounded-lg text-left block"
              >
                About Me
              </Link>
              <a 
                href="/#programs"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('programs');
                }}
                className="text-base text-muted-foreground hover:text-foreground transition-colors py-3 px-4 hover:bg-muted rounded-lg text-left"
              >
                Programs
              </a>
              <a 
                href="/#testimonials"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('testimonials');
                }}
                className="text-base text-muted-foreground hover:text-foreground transition-colors py-3 px-4 hover:bg-muted rounded-lg text-left"
              >
                Testimonials
              </a>
              <a 
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('contact');
                }}
                className="text-base text-muted-foreground hover:text-foreground transition-colors py-3 px-4 hover:bg-muted rounded-lg text-left"
              >
                Contact
              </a>
              <a
                href="/#book-session"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('book-session');
                }}
              >
                <Button 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground w-full py-6 text-base"
                >
                  Book Now
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
